// BL-187 Gates 3-4 — client-side live-tracking helpers.
//
// Ground truth: IntelAgent_BL187_GPS_LiveTracking_Build_v1_01.md.
//
// Pure helpers only — no React, no Mapbox, no Supabase — so the staleness and
// tier-filtering rules can be tested without a map or a network.

// How long a position stays believable. driver_locations receives a ping roughly
// every 15s while a driver is moving, with a 2-minute heartbeat even when parked
// (BL-187 Gate 2's throttle). So silence longer than the heartbeat plus a grace
// margin means we have lost them, not that they are sitting still.
export const POSITION_STALE_AFTER_MS = 240000;

/**
 * Decode a Mapbox polyline6 string into [lng, lat] pairs.
 *
 * The API returns the route line encoded; GL JS needs GeoJSON coordinates.
 * Precision 6 (not the classic 5) matches `geometries=polyline6` on the request —
 * decoding with the wrong precision does not error, it silently scatters the line
 * across the wrong part of the world by a factor of ten.
 */
export function decodePolyline(encoded, precision = 6) {
  if (typeof encoded !== 'string' || !encoded) return [];

  const factor = 10 ** precision;
  const coordinates = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 1;
    let shift = 0;
    let b;

    do {
      b = encoded.charCodeAt(index++) - 63 - 1;
      result += b << shift;
      shift += 5;
    } while (b >= 0x1f && index < encoded.length);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 1;
    shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63 - 1;
      result += b << shift;
      shift += 5;
    } while (b >= 0x1f && index < encoded.length);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    coordinates.push([lng / factor, lat / factor]);
  }

  return coordinates;
}

/**
 * Is this position old enough that we should stop implying it is current?
 *
 * Showing a four-minute-old dot as if it were live is how a client ends up
 * standing on a curb watching a car that is not coming.
 */
export function isPositionStale(recordedAt, now = Date.now(), maxAgeMs = POSITION_STALE_AFTER_MS) {
  if (!recordedAt) return true;
  const timestamp = new Date(recordedAt).getTime();
  if (Number.isNaN(timestamp)) return true;
  return now - timestamp > maxAgeMs;
}

/** "Updated 2 min ago" — the honest caption under a dot that may have drifted. */
export function formatPositionAge(recordedAt, now = Date.now()) {
  if (!recordedAt) return 'No position yet';
  const timestamp = new Date(recordedAt).getTime();
  if (Number.isNaN(timestamp)) return 'No position yet';

  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < 30) return 'Updated just now';
  if (seconds < 90) return 'Updated 1 min ago';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `Updated ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? 'Updated 1 hr ago' : `Updated ${hours} hr ago`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Client-tier view matrix
// ─────────────────────────────────────────────────────────────────────────────
// Module 11 Gate 6 locked what a CLIENT may see of a live trip: location, ETA and
// driver identity — and nothing else. v1.01 Gate 4 points at that same matrix.
//
// This is enforced by construction rather than by remembering: the client view
// renders only what buildClientTripView returns, so a field that is not on this
// list cannot leak into it by someone later passing a fuller object down. Note
// this is a presentation guard, not a security boundary — RLS is the boundary.

const CLIENT_VISIBLE_FIELDS = ['position', 'eta', 'driver'];

export function buildClientTripView({ position = null, eta = null, driver = null } = {}) {
  return {
    position: position
      ? {
          latitude: position.latitude,
          longitude: position.longitude,
          heading: position.heading ?? null,
          recordedAt: position.recordedAt ?? position.recorded_at ?? null,
        }
      : null,
    eta: eta
      ? {
          etaText: eta.etaText ?? null,
          distanceText: eta.distanceText ?? null,
          arrivalTime: eta.arrivalTime ?? null,
          geometry: eta.geometry ?? null,
        }
      : null,
    // Identity means a name and a vehicle they can recognise at the curb — never
    // a phone number, a payout rate, or an internal id.
    driver: driver
      ? {
          displayName: driver.displayName ?? null,
          vehicleDescription: driver.vehicleDescription ?? null,
          vehicleType: driver.vehicleType ?? null,
        }
      : null,
  };
}

export function clientVisibleFields() {
  return [...CLIENT_VISIBLE_FIELDS];
}

// ─────────────────────────────────────────────────────────────────────────────
// Vehicle-type dot
// ─────────────────────────────────────────────────────────────────────────────
// v1.00 named a "turquoise pulsing/glowing vehicle-type tracking dot" and v1.01
// carries it forward unchanged. Turquoise is the platform's Peacock accent
// (--brand-500, #03B7C9), so the dot follows the active white-label brand rather
// than hard-coding a colour.

const VEHICLE_GLYPHS = {
  sedan: '🚗',
  suv: '🚙',
  van: '🚐',
  sprinter: '🚐',
  limo: '🚘',
  bus: '🚌',
  shuttle: '🚐',
};

export function vehicleGlyph(vehicleType) {
  if (!vehicleType) return '🚗';
  return VEHICLE_GLYPHS[String(vehicleType).toLowerCase()] || '🚗';
}
